'use client';

import * as React from 'react';
import { Loader2, UserPlus } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useCreateCustomer } from '@/lib/api/customers';
import { useStoresList } from '@/lib/api/stores';
import { useTranslation } from 'react-i18next';

interface CreateCustomerDialogProps {
    onSuccess?: (customerId: string) => void;
}

export function CreateCustomerDialog({ onSuccess }: CreateCustomerDialogProps) {
    const { t } = useTranslation('customers');
    const [open, setOpen] = React.useState(false);
    const [email, setEmail] = React.useState('');
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [storeId, setStoreId] = React.useState('');

    const { mutateAsync: createCustomer, isPending } = useCreateCustomer();
    const { data: storesData } = useStoresList({ pagination: { page: 1, pageSize: 100 } });
    const stores = storesData?.stores ?? [];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !storeId) return;

        try {
            const result = await createCustomer({
                email,
                firstName: firstName || undefined,
                lastName: lastName || undefined,
                storeId,
            });
            onSuccess?.(result.data.id);
            handleClose();
        } catch {
            // Error handled by hook toast
        }
    };

    const handleClose = () => {
        if (isPending) return;
        setEmail('');
        setFirstName('');
        setLastName('');
        setStoreId('');
        setOpen(false);
    };

    return (
        <>
            <Button onClick={() => setOpen(true)} className="w-full sm:w-auto">
                <UserPlus className="mr-2 size-4" />
                {t('createDialog.button')}
            </Button>
            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>{t('createDialog.title')}</DialogTitle>
                        <DialogDescription>
                            {t('createDialog.description')}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">{t('createDialog.emailLabel')}</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder={t('createDialog.emailPlaceholder')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="firstName">{t('createDialog.firstNameLabel')}</Label>
                                <Input
                                    id="firstName"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="lastName">{t('createDialog.lastNameLabel')}</Label>
                                <Input
                                    id="lastName"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="store">{t('createDialog.storeLabel')}</Label>
                            <Select value={storeId} onValueChange={setStoreId}>
                                <SelectTrigger>
                                    <SelectValue placeholder={t('createDialog.storePlaceholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {stores.map((s) => (
                                        <SelectItem key={s.id} value={s.id}>
                                            {s.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
                                {t('createDialog.cancel')}
                            </Button>
                            <Button type="submit" disabled={!email || !storeId || isPending}>
                                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {t('createDialog.create')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
